import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";
import { isMasterAdmin } from "../utils/auth";

export const useDonorInventoryData = (filters = {}) => {
  const [loading, setLoading] = useState(true); // initial full-page load
  const [refreshing, setRefreshing] = useState(false); // subtle refresh indicator
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null); // track when data was last fetched
  const [data, setData] = useState({
    counters: {
      uniqueReceivers: 0,
      materialTypes: 0,
      totalQuantity: 0,
      totalRevenue: 0,
    },
    materialBreakdown: [],
    topReceivers: [],
    receiversByMaterial: {},
    timeline: [],
    transactions: [],
    availableMaterials: [],
    expiredListings: [],
    acceptanceSummary: [],
  });

  // app-level cache context for persistence across page navigations
  const { getCachedData, setCachedData } = useDataCache();

  const lastFetchRef = useRef(null);
  const isFetching = useRef(false);
  const isInitialLoad = useRef(true);

  const fetchData = useCallback(async (filterParams = {}) => {
    // prevent concurrent fetches
    if (isFetching.current) return;
    isFetching.current = true;

    // show refreshing indicator (not full-page) for subsequent fetches
    if (!isInitialLoad.current) {
      setRefreshing(true);
    }

    try {
      const params = new URLSearchParams();

      if (filterParams.dateFrom) {
        params.append("date_from", filterParams.dateFrom);
      }
      if (filterParams.dateTo) {
        params.append("date_to", filterParams.dateTo);
      }
      if (filterParams.materials && filterParams.materials.length > 0) {
        params.append("materials", filterParams.materials.join(","));
      }
      if (filterParams.period) {
        params.append("period", filterParams.period);
      }
      if (isMasterAdmin()) {
        params.append("show_all", "true");
      }

      const url = `${config.apiUrl}/donor/inventory/stats?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        credentials: "include",
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const newData = {
          counters: result.counters || {
            uniqueReceivers: 0,
            materialTypes: 0,
            totalQuantity: 0,
            totalRevenue: 0,
          },
          materialBreakdown: result.materialBreakdown || [],
          topReceivers: result.topReceivers || [],
          receiversByMaterial: result.receiversByMaterial || {},
          timeline: result.timeline || [],
          transactions: result.transactions || [],
          availableMaterials: result.availableMaterials || [],
          expiredListings: result.expiredListings || [],
          acceptanceSummary: result.acceptanceSummary || [],
        };
        setData(newData);
        setError(null);
        lastFetchRef.current = Date.now();
        const timestamp = Date.now();
        setLastUpdated(timestamp);

        // Store in context cache for persistence across page navigations
        setCachedData(CACHE_KEYS.DONOR_INVENTORY, newData, timestamp);
      } else {
        setError(result.message || "Failed to load inventory data");
      }
    } catch (err) {
      console.error("Error fetching donor inventory:", err);
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetching.current = false;
      isInitialLoad.current = false;
    }
  }, [setCachedData]);

  // initial fetch - check context cache first
  useEffect(() => {
    // Check context cache first (persists across page navigations)
    const contextCache = getCachedData(CACHE_KEYS.DONOR_INVENTORY);
    if (contextCache && contextCache.data) {
      console.log("Using context-cached data for DonorInventory");
      setData(contextCache.data);
      setLastUpdated(contextCache.timestamp);
      setLoading(false);
      isInitialLoad.current = false;
      return;
    }

    // No context cache, fetch from API
    fetchData(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // manual refresh function
  const refresh = useCallback(
    (newFilters = filters) => {
      setRefreshing(true);
      fetchData(newFilters);
    },
    [fetchData, filters]
  );

  return {
    loading,
    refreshing,
    error,
    data,
    refresh,
    lastUpdated,
    counters: data.counters,
    materialBreakdown: data.materialBreakdown,
    topReceivers: data.topReceivers,
    receiversByMaterial: data.receiversByMaterial,
    timeline: data.timeline,
    transactions: data.transactions,
    availableMaterials: data.availableMaterials,
    expiredListings: data.expiredListings,
    acceptanceSummary: data.acceptanceSummary,
  };
};

export default useDonorInventoryData;
